import {
  useEffect, useState,
} from 'react'
import {
  areFlagsSettledForIdentifiedUser,
  type FeatureFlagName,
  flagsSettledForIdentifiedUser,
  getFeatureFlagVariantSync,
  onFeatureFlagsLoaded,
} from '../analytics/posthog'

/**
 * An experiment's arm for the signed-in person — read, and the exposure that
 * enrols them recorded, only once the flags are theirs.
 *
 * posthog-js's `useFeatureFlagVariantKey` reads on its first render whatever
 * flags are loaded, and straight after a sign-in those are still the anonymous
 * visitor's: `identify()` swaps the distinct id at once but reloads flags a
 * round-trip later. The first exposure then goes out under the signed-in id
 * carrying the anonymous bucket, and a second follows with the real one when
 * the reload lands — the person is counted in both arms, so the analysis drops
 * them, and they have watched the page switch layouts under them. On
 * experiment 462588 that was 111 of 508 exposed people, every conflicting pair
 * within a second of `sign_in_completed` (GET-16313).
 *
 * On a load that did not sign in the flags are already the person's, so the
 * arm is read on the first render exactly as before. Straight after a sign-in
 * this returns `undefined` — the caller's default arm — until the identified
 * flags arrive, then records one exposure, for the right arm. The wait fails
 * open after identify's timeout. That default paint is one-sided: only people
 * assigned to a non-default arm see it and then reflow. Before, a random half
 * of every arm saw the wrong layout, so read a sign-in-heavy surface's arms
 * as not paint-symmetric.
 */
export function useExperimentVariant(flag: FeatureFlagName): string | boolean | undefined {
  const [variant, setVariant] = useState(() => (
    areFlagsSettledForIdentifiedUser() ? getFeatureFlagVariantSync(flag) : undefined
  ))

  useEffect(() => {
    let active = true
    let unsubscribe: (() => void) | undefined
    void flagsSettledForIdentifiedUser().then(() => {
      if (!active) return
      unsubscribe = onFeatureFlagsLoaded(() => setVariant(getFeatureFlagVariantSync(flag)))
    })
    return () => {
      active = false
      unsubscribe?.()
    }
  }, [flag])

  return variant
}
